import { useEffect, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, AreaChart, Area,
} from "recharts";
import { DollarSign, TrendingUp, Shield } from "lucide-react";
import { Header } from "@/pages/Operations";
import api from "@/lib/api";

const tooltipStyle = { background: "#0E1019", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 };

export default function CostGovernor() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/cost/summary").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const pct = data ? Math.round((data.spent / data.budget) * 100) : 0;

  return (
    <div data-testid="cost-governor-view" className="p-5 sm:p-8 max-w-[1400px] mx-auto">
      <Header
        icon={DollarSign}
        eyebrow="FinOps · Budget Shield"
        title="Cost Governor"
        subtitle="Track burn rate, allocate spend per engine and auto-throttle runaway costs."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 glass rounded-2xl p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="font-display font-semibold text-sm">Monthly Budget</span>
            <span className="text-xs font-mono text-slate-500">threshold ${data?.budget?.toLocaleString()}</span>
          </div>
          <div className="text-3xl font-display font-extrabold">
            ${data?.spent?.toLocaleString()} <span className="text-slate-600 text-lg">/ ${data?.budget?.toLocaleString()}</span>
          </div>
          <div className="mt-4 h-3 rounded-full bg-white/5 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${pct > 90 ? "bg-red-500" : "bg-gradient-to-r from-purple-500 to-cyan-500"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-xs font-mono text-slate-500">
            <span>{pct}% consumed</span>
            <span>projected ${data?.projected?.toLocaleString()}</span>
          </div>
        </div>

        <div className="glass rounded-2xl p-6 flex flex-col justify-center gap-4">
          <div className="flex items-center gap-3">
            <TrendingUp size={20} className="text-amber-400" />
            <div>
              <div className="text-lg font-display font-bold">{data?.burn_rate}</div>
              <div className="text-xs text-slate-500">Current burn rate</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Shield size={20} className={data?.throttle_active ? "text-red-400" : "text-emerald-400"} />
            <div>
              <div className="text-lg font-display font-bold">{data?.throttle_active ? "Active" : "Standby"}</div>
              <div className="text-xs text-slate-500">Auto-throttle policy</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="glass rounded-2xl p-5">
          <span className="font-display font-semibold text-sm">Cost Per Engine</span>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data?.by_engine || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="engine" stroke="#64748B" fontSize={10} angle={-15} textAnchor="end" height={50} />
              <YAxis stroke="#64748B" fontSize={11} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="cost" radius={[6, 6, 0, 0]} fill="#8B5CF6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="glass rounded-2xl p-5">
          <span className="font-display font-semibold text-sm">Daily Spend · 14d</span>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data?.daily || []}>
              <defs>
                <linearGradient id="gCost" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#06B6D4" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#06B6D4" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="day" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="cost" stroke="#06B6D4" fill="url(#gCost)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
